import React, { useContext, useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import UserContext from '../../contexts/UserContext'
import { hasModulePermission } from '../../config/modulesByTenant'
import { useTenant } from '../../contexts/TenantContext'
import GlobalLoadingOverlay from '../GlobalLoadingOverlay'
import SectionContext from '../../contexts/SectionContext'
import { hasPermission as hasPermissionInList, resolveEffectivePermissions } from '../../utils/permissions'

interface PermissionGuardProps {
  children: React.ReactNode
  permission: string
  dependencies?: string[]
  redirectTo?: string
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  dependencies = [],
  redirectTo = '/panel'
}) => {
  const { user, token, isLoadingAuth } = useContext(UserContext)
  const { permissions: sectionPermissions } = useContext(SectionContext)
  const { tenantId } = useTenant()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    if (!isLoadingAuth && token) {
      const timer = setTimeout(() => {
        setIsChecking(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isLoadingAuth, token])

  useEffect(() => {
    console.log('🔐 [PERMISSION-GUARD] Snapshot', {
      pathname: location.pathname,
      permission,
      dependencies,
      redirectTo,
      tenantId,
      hasToken: !!token,
      isLoadingAuth,
      isChecking,
      userId: user?.id || user?.sub,
      userRole: user?.role,
      userPermissions: user?.permissions,
      sectionPermissions,
    })
  }, [
    location.pathname,
    permission,
    redirectTo,
    tenantId,
    token,
    isLoadingAuth,
    isChecking,
    user?.id,
    user?.sub,
    user?.role,
    user?.permissions,
    sectionPermissions,
    dependencies,
  ])

  if (isLoadingAuth || isChecking) {
    console.log('🔐 [PERMISSION-GUARD] Overlay por carregamento', {
      pathname: location.pathname,
      permission,
      isLoadingAuth,
      isChecking,
    })
    return <GlobalLoadingOverlay />
  }

  if (token && tenantId === null) {
    console.log('🔐 [PERMISSION-GUARD] Overlay por tenantId nulo com token', {
      pathname: location.pathname,
      permission,
    })
    return <GlobalLoadingOverlay />
  }

  if (token && (!user || (!user.id && !user.sub))) {
    console.log('🔐 [PERMISSION-GUARD] Overlay por usuário ausente com token', {
      pathname: location.pathname,
      permission,
    })
    return <GlobalLoadingOverlay />
  }

  const effectivePermissions = resolveEffectivePermissions(user?.permissions, sectionPermissions)

  console.log('🔐 [PERMISSION-GUARD] Permissões efetivas', {
    pathname: location.pathname,
    permission,
    effectivePermissions,
  })

  if (effectivePermissions.length === 0) {
    console.warn('🔐 [PERMISSION-GUARD] Redirecionando por ausência de permissões efetivas', {
      pathname: location.pathname,
      permission,
      redirectTo,
      userPermissions: user?.permissions,
      sectionPermissions,
    })
    return <Navigate to={redirectTo} replace />
  }

  const evaluateAccess = () => {
    if (tenantId === 2) {
      if (hasPermissionInList(effectivePermissions, permission)) {
        return { allowed: true, reason: 'fashion-main-permission' }
      }
      
      if (permission === 'router' || permission === 'services') {
        return { allowed: true, reason: 'fashion-special-case' }
      }
      
      return { allowed: false, reason: 'fashion-missing-permission' }
    }
    
    const moduleConfig = {
      name: '',
      path: '',
      image: '',
      section: '',
      permission,
      dependencies: dependencies.length > 0 ? dependencies : undefined
    }
    
    const allowed = hasModulePermission(moduleConfig, effectivePermissions)
    return { allowed, reason: allowed ? 'industrial-main-permission' : 'industrial-missing-permission' }
  }

  const accessEvaluation = evaluateAccess()

  console.log('🔐 [PERMISSION-GUARD] Resultado da avaliação', {
    pathname: location.pathname,
    permission,
    tenantId,
    allowed: accessEvaluation.allowed,
    reason: accessEvaluation.reason,
    effectivePermissions,
  })

  if (!accessEvaluation.allowed) {
    console.warn('🔐 [PERMISSION-GUARD] Redirecionando por falta de acesso', {
      pathname: location.pathname,
      permission,
      redirectTo,
      reason: accessEvaluation.reason,
      effectivePermissions,
      tenantId,
    })
    return <Navigate to={redirectTo} replace />
  }

  console.log('🔐 [PERMISSION-GUARD] Acesso liberado', {
    pathname: location.pathname,
    permission,
    tenantId,
    reason: accessEvaluation.reason,
  })

  return <>{children}</>
}

export default PermissionGuard
